import type { ReactElement } from 'react';

import type { MapObjectInstance } from '../schema/map';
import { getObjectDefinition } from './objectRegistry';

/**
 * Opaque per-frame runtime context a game may thread into its renderers
 * (e.g. which radios are on, which jumpscares have fired). The engine never
 * names or reads these keys; a game casts this to its own typed shape.
 */
export type AssetRuntimeState = Record<string, unknown>;

export interface AssetRendererProps {
  instance: MapObjectInstance;
  runtimeState?: AssetRuntimeState;
}

export type AssetRenderer = (props: AssetRendererProps) => ReactElement | null;

/**
 * assetId -> renderer. Games and built-in packs populate this; the editor,
 * thumbnail renderer, and runtime scene all resolve geometry through it. This
 * is the inversion that lets the engine render placed objects without importing
 * any game code.
 */
const RENDERERS = new Map<string, AssetRenderer>();

export function registerRenderers(renderers: Record<string, AssetRenderer>): void {
  for (const [assetId, renderer] of Object.entries(renderers)) {
    RENDERERS.set(assetId, renderer);
  }
}

export function getRenderer(assetId: string): AssetRenderer | null {
  return RENDERERS.get(assetId) ?? null;
}

export function hasRenderer(assetId: string): boolean {
  return RENDERERS.has(assetId);
}

/**
 * Neutral fallback for an asset whose id has a definition (so we know its
 * bounds) but no registered renderer - a translucent bounding box. This is the
 * graceful default for unregistered assets, not a placeholder for missing work.
 */
function FallbackGizmo({ assetId }: { assetId: string }): ReactElement {
  const definition = getObjectDefinition(assetId);
  const size = definition?.bounds.size ?? [1, 1, 1];
  const centerY = definition?.editor.pivot === 'base-center' ? size[1] / 2 : 0;
  return (
    <mesh position={[0, centerY, 0]}>
      <boxGeometry args={size} />
      <meshStandardMaterial color="#8a93a0" transparent opacity={0.35} wireframe />
    </mesh>
  );
}

/**
 * Resolve and render an asset instance. The instance transform is expected to
 * be applied by the caller's parent group, so renderers position content at the
 * local origin.
 */
export function renderAsset(
  instance: MapObjectInstance,
  runtimeState?: AssetRuntimeState,
): ReactElement | null {
  const renderer = RENDERERS.get(instance.assetId);
  if (!renderer) {
    return <FallbackGizmo assetId={instance.assetId} />;
  }
  return renderer({ instance, runtimeState });
}
